import { Control, FieldError, FieldValues, UseFormSetValue } from 'react-hook-form';
import { useTheme } from 'styled-components';

import CreatableSelect from 'coreOld/components/CreatableSelect';
import { Size } from 'coreOld/enums';
import { IOption } from 'coreOld/types';
import { isEmptyObject } from 'coreOld/utils';

import { createCustomStyles } from './selectStyle';
import SelectView from './SelectView';
import SelectWithControllerView from './SelectWithControllerView';

interface IProps {
  name: string;
  value?: string | number | string[] | number[];
  defaultValue?: string | number | string[] | number[];
  placeholder?: string;
  menuIsOpen?: boolean;
  isMulti?: boolean;
  required?: boolean;
  disabled?: boolean;
  editable?: boolean;
  showDropdownIcon?: boolean;
  size?: Size;
  fontInherit?: boolean;
  hasBorder?: boolean;
  control?: Control<FieldValues, object>;
  setValue?: UseFormSetValue<FieldValues>;
  useGetOptions?;
  getValue?;
  getLabel?;
  options?;
  loadingOptions?: boolean;
  onChange?;
  requestPayload?;
  onKeyDown?;
  isClearable?: boolean;
  noOption?: Record<string, string | number>;
  error?: FieldError | string;
  showFieldError?: boolean;
  isCreatable?: boolean;
  createOption?: (label: string) => IOption;
  autoFocus?: boolean;
}

const noopHook = () => ({ records: [], isLoading: false, isFetching: false });

const Select = (props: IProps) => {
  const {
    options,
    loadingOptions = false,
    useGetOptions = noopHook,
    onChange,
    getValue = (option) => option.value,
    getLabel = (option) => option.label,
    size = Size.LG,
    requestPayload,
    hasBorder = true,
    fontInherit = false,
    placeholder = '',
    noOption = {},
    error,
    isCreatable = false,
  } = props;

  let data = [];
  let loading = loadingOptions;
  let fetching = false;

  const { records, isLoading, isFetching } = useGetOptions(requestPayload);

  if (!isEmptyObject(noOption) && !isLoading && !isFetching) {
    data = [noOption];
  }

  data = [...data, ...records];
  loading = isLoading;
  fetching = isFetching;

  const theme = useTheme();
  const customStyles = createCustomStyles(theme, size, fontInherit, hasBorder, Boolean(error));

  const mapDataToFilters = (options, onChange) => {
    const data = options.map((item) => getValue(item) || item);

    onChange(data);
  };

  const handleGetValue = (option?: IOption) => (option ? getValue(option) : null);

  const handleGetLabel = (option?: IOption) => {
    if (!getLabel(option)) {
      option = data.find((item) => getValue(item) === option);
    }

    return option ? getLabel(option) : placeholder;
  };

  if (isCreatable) {
    return (
      <CreatableSelect
        customStyles={customStyles}
        mapDataToFilters={mapDataToFilters}
        onChange={onChange}
        options={options || data}
        isLoadingOptions={loading || fetching}
        {...props}
        getValue={handleGetValue}
        getLabel={handleGetLabel}
      />
    );
  }

  if (props.control) {
    return (
      <SelectWithControllerView
        customStyles={customStyles}
        mapDataToFilters={mapDataToFilters}
        onChange={onChange}
        options={options || data}
        isLoadingOptions={loading || fetching}
        {...props}
        getValue={handleGetValue}
        getLabel={handleGetLabel}
      />
    );
  }

  return (
    <SelectView
      customStyles={customStyles}
      mapDataToFilters={mapDataToFilters}
      onChange={onChange}
      options={options || data}
      isLoadingOptions={loading || fetching}
      {...props}
      getValue={handleGetValue}
      getLabel={handleGetLabel}
    />
  );
};

export default Select;
